// 一局胡牌結算的純計分編排
// 從 CRound.endRoundHu 抽出：把「huInfos → THuResult[]」這段計分編排（findHuPatterns →
// scoreBestPattern → 組 THuResult）移出狀態機。純函式：只讀輸入、回傳結果，不碰 state、不 emit。

import type { THuResult } from './summary';
import type { THand } from './hand';
import { findHuPatterns } from './hu';
import { scoreBestPattern, scoreEightFlowers, type TScoreContext } from './scoring';
import type { TTile } from './tile';
import type { TSeat, TWind } from './types';

/** 一筆待結算的胡牌（自摸或放槍）。 */
export const SpecialWin = {
  EightFlowers: 'eight-flowers',
} as const;
export type TSpecialWin = (typeof SpecialWin)[keyof typeof SpecialWin];

export type THuInfo = {
  readonly seat: TSeat;
  readonly isSelfDraw: boolean;
  /** 放槍者；自摸時為 null。 */
  readonly loserSeat: TSeat | null;
  readonly winTile: TTile;
  /** 胡牌是否發生在別家宣告加槓、第四張牌尚未正式入槓時。 */
  readonly isRobbingKong?: boolean;
  /** 不需要一般五面子一雀頭的特殊胡牌。 */
  readonly specialWin?: TSpecialWin;
};

/** 計分所需的一局固定脈絡（不含 per-pattern 欄位）。 */
export type TSettleContext = {
  readonly hands: readonly [THand, THand, THand, THand];
  readonly dealerSeat: TSeat;
  readonly seatWinds: readonly [TWind, TWind, TWind, TWind];
  readonly prevailingWind: TWind;
  readonly declaredTenpai: ReadonlySet<TSeat>;
  /** 莊家連莊次數 n（連 N 拉 N → 2n+1 台；0 = 沒連莊）。 */
  readonly dealerStreak: number;
  /** 胡牌當下牌牆剩餘張數（海底 / 河底判定）。 */
  readonly wallRemaining: number;
  /** 自摸的那張是否為槓後補牌（槓上開花判定）。 */
  readonly isAfterKong: boolean;
};

/**
 * 把唯一 huInfo 算出最佳台型與台數，組成相容既有 UI 的 THuResult[]。
 * 找不到合法台型（理論上不會發生）的那筆會被略過。
 */
export const computeHuResults = (
  huInfos: ReadonlyArray<THuInfo>,
  ctx: TSettleContext
): THuResult[] => {
  const results: THuResult[] = [];
  for (const info of huInfos) {
    const hand = ctx.hands[info.seat];
    if (info.specialWin === SpecialWin.EightFlowers) {
      const specialScore = scoreEightFlowers(ctx.dealerSeat === info.seat, ctx.dealerStreak);
      results.push({
        seat: info.seat,
        isSelfDraw: true,
        loserSeat: null,
        winTile: info.winTile,
        scoreItems: specialScore.items,
        total: specialScore.total,
      });
      continue;
    }
    const patterns = findHuPatterns(hand, info.winTile, info.isSelfDraw);
    const fixedCtx: Omit<TScoreContext, 'pattern'> = {
      hand,
      isDealer: ctx.dealerSeat === info.seat,
      dealerStreak: ctx.dealerStreak,
      seatWind: ctx.seatWinds[info.seat],
      prevailingWind: ctx.prevailingWind,
      wallRemaining: ctx.wallRemaining,
      isAfterKong: ctx.isAfterKong,
      isRobbingKong: info.isRobbingKong === true,
      declaredTenpai: ctx.declaredTenpai.has(info.seat),
    };
    const best = scoreBestPattern(patterns, fixedCtx);
    if (!best) continue;

    results.push({
      seat: info.seat,
      isSelfDraw: info.isSelfDraw,
      loserSeat: info.loserSeat,
      winTile: info.winTile,
      scoreItems: best.items,
      total: best.total,
    });
  }
  return results;
};
